import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PlayerSearch from './PlayerSearch';
import { needsTrackmanSyncNotice, RECRUITING_BLOCKED_MESSAGE } from '../utils/trackmanAccess';
import './Navigation.css';

// Top menu definition. Items: { label, to } links, { heading } group labels, and optional
// `show(user)` (hide) / `blocked(user)` (grey out with a reason) predicates.
const isAdmin = (u) => u?.role === 'Admin';
// South Alabama logins (real accounts; Admins too, to check the page)
const isSal = (u) => !u?.demo && (u?.team === 'SAL_JAG' || isAdmin(u));
const isSalCoach = (u) => !u?.demo && u?.team === 'SAL_JAG' && !isAdmin(u);
const MENUS = [
  {
    key: 'postgame', label: 'Post Game', items: [
      { heading: 'Hitting' },
      { label: 'Post Game Hitter Reports', to: '/post-game-hitter-reports' },
      { label: 'Batter Upload', to: '/batter-upload' },
      { heading: 'Pitching' },
      { label: 'Post Game Pitching Report', to: '/post-game-pitching-reports' },
      { label: 'Pitcher Upload', to: '/pitcher-upload' },
      { heading: 'Defense' },
      { label: 'Catcher Framing Reports', to: '/catcher-framing-reports' },
    ],
  },
  {
    key: 'scouting', label: 'Scouting', items: [
      { label: 'Lineup Report', to: '/lineup-card' },
      { label: 'Opposing Pitching Report', to: '/opposing-pitching-report' },
      { label: 'Infield/Outfield Report', to: '/infield-outfield-report' },
      { label: 'Pitcher Video', to: '/pitcher-video', show: (u) => !isSalCoach(u) },
    ],
  },
  {
    key: 'dugout', label: 'Dugout', items: [
      { label: 'Post Game', to: '/dugout/post-game' },
      { label: 'Scouting', to: '/dugout/scouting' },
      { label: 'South Alabama', to: '/dugout/south-alabama', show: isSal },
      { label: 'Pitch Retagger', to: '/pitch-retagging', show: (u) => !u?.demo && isAdmin(u) },
    ],
  },
  {
    key: 'recruiting', label: 'Recruiting', items: [
      { label: 'Portal', to: '/portal', blocked: needsTrackmanSyncNotice },
      { label: 'JUCO Leaderboard', to: '/juco-leaderboard', blocked: needsTrackmanSyncNotice },
      { label: 'Leaderboard', to: '/leaderboard', show: isSal },
      { label: 'High School', to: '/high-school', show: isAdmin },
    ],
  },
];

function MenuEntry({ item, user, onPick }) {
  if (item.heading) return <div className="topbar-group">{item.heading}</div>;
  if (item.show && !item.show(user)) return null;
  if (item.blocked && item.blocked(user)) {
    return <span className="topbar-option is-disabled" aria-disabled="true" title={RECRUITING_BLOCKED_MESSAGE}>{item.label}</span>;
  }
  return <Link to={item.to} className="topbar-option" onClick={onPick}>{item.label}</Link>;
}

export default function Navigation() {
  const { logout, currentUser } = useAuth();
  const [openMenu, setOpenMenu] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const barRef = useRef(null);

  const closeAll = () => { setOpenMenu(null); setDrawer(false); };

  useEffect(() => {
    const onOutside = (e) => { if (barRef.current && !barRef.current.contains(e.target)) closeAll(); };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  const signOut = async () => {
    closeAll();
    try { await logout(); } catch (err) { console.error('Sign out failed:', err); }
  };

  return (
    <nav className="topbar">
      <div className="topbar-inner" ref={barRef}>
        <button
          type="button"
          className={`topbar-burger${drawer ? ' is-open' : ''}`}
          aria-label="Toggle menu"
          aria-expanded={drawer}
          aria-controls="topbar-links"
          onClick={() => { setDrawer((v) => !v); setOpenMenu(null); }}
        >
          <i /><i /><i />
        </button>

        <Link to="/" className="topbar-brand" onClick={closeAll}>
          <img src="/images/dugoutscout.png" alt="Dugout Scout" />
        </Link>

        <PlayerSearch />

        <ul id="topbar-links" className={`topbar-links${drawer ? ' is-open' : ''}`}>
          <li><Link to="/" className="topbar-link" onClick={closeAll}>Home</Link></li>
          {MENUS.map((menu) => (
            <li key={menu.key} className="topbar-menu">
              <button type="button" className="topbar-link topbar-trigger"
                onClick={() => setOpenMenu((cur) => (cur === menu.key ? null : menu.key))}>
                {menu.label}
                <span className={`topbar-caret${openMenu === menu.key ? ' is-open' : ''}`}>▼</span>
              </button>
              {openMenu === menu.key && (
                <div className="topbar-panel">
                  {menu.items.map((item, i) => (
                    <MenuEntry key={item.to || `${item.heading}-${i}`} item={item} user={currentUser} onPick={closeAll} />
                  ))}
                </div>
              )}
            </li>
          ))}
          <li><button type="button" className="topbar-signout" onClick={signOut}>Sign Out</button></li>
        </ul>
      </div>
    </nav>
  );
}
